/**
 * Taxonomy Store (S1.1)
 *
 * Zustand store for the taxonomy admin surface: tree filters + UI state.
 * Node SELECTION is deliberately NOT here — it is route-derived via the
 * `?node=` search param on /auth/product-taxonomy (route-derived selection
 * tenet), so deep links restore the pane for free.
 *
 * Data itself lives in the ahooks cache (`/api/taxonomy/*` keys, see
 * useTaxonomy.ts); mutations invalidate those caches (store-first contract).
 */

import { create } from 'zustand';
import { devtools } from 'zustand/middleware';

export interface TaxonomyFilters {
  /** Type-to-filter term matched against code / nameEn / nameZh. */
  search: string;
  /** Admin default hides deactivated nodes; toggle reveals them grayed. */
  includeInactive: boolean;
}

export interface TaxonomyState {
  filters: TaxonomyFilters;

  setSearch: (search: string) => void;
  setIncludeInactive: (includeInactive: boolean) => void;
  resetFilters: () => void;
}

const initialFilters: TaxonomyFilters = {
  search: '',
  includeInactive: false,
};

export const useTaxonomyStore = create<TaxonomyState>()(
  devtools(
    (set) => ({
      filters: { ...initialFilters },

      setSearch: (search) =>
        set((state) => ({ filters: { ...state.filters, search } }), false, 'taxonomy/setSearch'),

      setIncludeInactive: (includeInactive) =>
        set(
          (state) => ({ filters: { ...state.filters, includeInactive } }),
          false,
          'taxonomy/setIncludeInactive'
        ),

      resetFilters: () => set({ filters: { ...initialFilters } }, false, 'taxonomy/resetFilters'),
    }),
    { name: 'TaxonomyStore' }
  )
);
